import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableSortLabel,
  TextField,
} from "@mui/material";
import { ReactComponent as NextIcon } from "../../../../assets/icons/next.svg";
import { ReactComponent as PreviousIcon } from "../../../../assets/icons/previous.svg";
import styles from "../../../../assets/MUIStyles/dynamicTableStyles";
import { shortenString } from "../../../../Constant/helper";
import { PAGE_SIZE_50 } from "../Common";
import "../../../../assets/css/page/DynamicTable.scss";
import EmptyState from "../../Common/EmptyState";
import PageLoader from "../PageLoader";
import { commonStyles } from "../../../../assets/MUIStyles/commonStyles";

type DynamicTableProps = {
  columns: any[];
  data: any[];
  loading?: boolean;
  totalPages?: number;
  currentPage?: number;
  handlePageChange?: (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => void;
  handlePrevious?: () => void;
  handleNext?: () => void;
  noDataAvailableMessage?: string;
  style?: any;
  showTotalRows?: boolean;
  totalRowCount?: number;
  pagination?: boolean;
  rowCount?: number;
  highlightRowCondition?: (row: any, rowIndex: number) => boolean;
  highlightRowColor?: any;
  onRowClick?: (row: any) => void;
  handleJumpPageChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  jumpPage?: string;
  handleJumpPage?: () => void;
  jumpTo?: boolean;
  showPagination?: boolean;
};

const DynamicTable = ({
  columns,
  data,
  loading = false,
  totalPages = 0,
  currentPage = 0,
  handlePageChange = () => {},
  handlePrevious = () => {},
  handleNext = () => {},
  noDataAvailableMessage = "No Data Available",
  style = {},
  showTotalRows = false,
  totalRowCount = 0,
  pagination = true,
  rowCount = 0,
  highlightRowCondition = () => false,
  highlightRowColor = {},
  onRowClick,
  handleJumpPageChange = () => {},
  jumpPage = "",
  handleJumpPage = () => {},
  jumpTo = false,
  showPagination = true,
}: DynamicTableProps) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  const handleSort = (field: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== field) {
        return { key: field, direction: "asc" };
      }
      if (prev.direction === "asc") {
        return { key: field, direction: "desc" };
      }
      return null;
    });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;
    return [...data].sort((a, b) => {
      const valA = a[sortConfig.key] ?? "";
      const valB = b[sortConfig.key] ?? "";
      if (typeof valA === "string" && typeof valB === "string") {
        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return sortConfig.direction === "asc" ? valA - valB : valB - valA;
    });
  }, [data, sortConfig]);

  const disablePrevButton = currentPage === 1;

  const disableNextButton =
    currentPage === totalPages || rowCount < PAGE_SIZE_50;

  return (
    <>
      {loading && (
        <Box sx={styles.loaderContainer}>
          <PageLoader height="100%" />
        </Box>
      )}
      {sortedData?.length > 0 ? (
        <Box>
          <TableContainer
            elevation={0}
            component={Paper}
            sx={{
              ...styles.tableContainer,
              maxHeight: "70vh",
              overflowY: "auto",
              overflowX: "auto",
              whiteSpace: "nowrap",
              ...style,
            }}
          >
            <Table stickyHeader>
              <TableHead sx={{ position: "sticky", top: 0, zIndex: 3 }}>
                <TableRow>
                  {columns?.map((col, index) => (
                    <TableCell
                      key={index}
                      sx={styles.header}
                      sortDirection={
                        sortConfig?.key === col.field
                          ? sortConfig?.direction
                          : false
                      }
                    >
                      {col.sortable && col.field ? (
                        <TableSortLabel
                          active={sortConfig?.key === col.field}
                          direction={sortConfig?.direction || "asc"}
                          onClick={() => handleSort(col.field)}
                          sx={{
                            "& .MuiTableSortLabel-icon": {
                              opacity: 1,
                            },
                          }}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    hover={!!onRowClick}
                    onClick={(e: any) => {
                      if (
                        e.target.closest("input[type='checkbox']") ||
                        e.target.closest("button") ||
                        e.target.closest(".MuiIconButton-root")
                      ) {
                        return;
                      }

                      if (onRowClick) onRowClick(row);
                    }}
                    sx={[
                      {
                        ...styles.tableRow,
                        "&:hover": {
                          cursor: onRowClick ? "pointer" : "default",
                        },
                      },
                      highlightRowCondition(row, rowIndex) &&
                        (Object.keys(highlightRowColor)?.length > 0
                          ? highlightRowColor
                          : styles.highlightedRow),
                    ]}
                  >
                    {columns?.map((col, colIndex) => (
                      <TableCell
                        key={colIndex}
                        sx={{
                          ...styles.cell,
                          ...(typeof col.styles === "function"
                            ? col.styles(row)
                            : col.styles || {}),
                        }}
                        onClick={(e) => {
                          if (onRowClick && col?.isAction) {
                            e.stopPropagation();
                          }
                        }}
                      >
                        {col?.render
                          ? col?.render(row, rowIndex)
                          : row[col?.field]
                          ? shortenString(row[col?.field], 25)
                          : "--"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {showPagination && (
            <Stack
              direction="row"
              sx={styles.bottomContainer}
              className="pagination-container"
            >
              <Box sx={styles.countContainer}>
                <Typography className="font-12-grey">Total </Typography>
                <Typography component="span" sx={styles.countMainContainer}>
                  {`${rowCount} row(s)`}
                </Typography>
              </Box>

              {pagination && (
                <>
                  {totalRowCount > PAGE_SIZE_50 && (
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        shape="rounded"
                        size="medium"
                        variant="outlined"
                        siblingCount={0}
                        boundaryCount={2}
                        hidePrevButton
                        hideNextButton
                        sx={styles.pagination}
                        className="pagination"
                      />
                      {jumpTo && (
                        <Box className="flex-align-center" sx={{ gap: 1 }}>
                          <Typography className="font-12-grey">
                            Jump to:
                          </Typography>
                          <TextField
                            type="number"
                            value={jumpPage.trim()}
                            onChange={handleJumpPageChange}
                            sx={{ ...commonStyles.paginationInput, width: 50 }}
                            inputProps={{ min: 1 }}
                            placeholder={`1-${totalPages}`}
                          />
                          <Button variant="outlined" onClick={handleJumpPage}>
                            Go
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  )}

                  {!(disablePrevButton && disableNextButton) && (
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <Button
                        variant="outlined"
                        startIcon={
                          <PreviousIcon
                            style={{
                              opacity: disablePrevButton ? 0.5 : 1,
                              color: disablePrevButton ? "#B0B0B0" : "#666666",
                            }}
                          />
                        }
                        onClick={handlePrevious}
                        disabled={disablePrevButton}
                        sx={styles.paginationButtons}
                      >
                        Previous
                      </Button>

                      <Button
                        variant="outlined"
                        endIcon={
                          <NextIcon
                            style={{
                              opacity: disableNextButton ? 0.5 : 1,
                              color: disableNextButton ? "#B0B0B0" : "#666666",
                            }}
                          />
                        }
                        onClick={handleNext}
                        disabled={disableNextButton}
                        sx={styles.paginationButtons}
                      >
                        Next
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}
        </Box>
      ) : (
        <EmptyState message={noDataAvailableMessage} />
      )}
    </>
  );
};

export default DynamicTable;
